import { useState, useEffect, useRef } from 'react';
import type { ChangeEvent } from 'react';

import {
  Flex,
  Input,
  NumberInput,
  Checkbox,
  RadioButton,
  RadioButtonGroup,
  useI18n
} from '@pega/cosmos-react-core';

import { StyledFormDialog } from './Editor.styles';
import type { ImageCustomSourceConfig } from './Editor.types';

interface ImageEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (attrs: ImageAttributes) => void;
  initialAttributes: ImageAttributes;
  target?: HTMLElement | null;
  imageCustomSource?: ImageCustomSourceConfig;
}

export interface ImageAttributes {
  src: string;
  alt?: string;
  width?: number;
  height?: number;
  actionUrl?: string;
}

const isFieldReferenceUrl = (value: string) => {
  return value.includes('<pega-reference') || value.includes('&lt;pega-reference');
};

/**
 * Normalize escaped pega-reference markup to raw form.
 * Prevents double-escaping when the actionUrl is re-serialized into href attributes.
 * E.g., "&lt;pega-reference...&gt;" → "<pega-reference...>"
 */
const normalizeEscapedPegaReference = (value: string): string => {
  return value
    .replaceAll('&quot;', '"')
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>')
    .replaceAll('&amp;', '&');
};

const parseCustomSourceFromHref = (href: string) => {
  const decodedHref = normalizeEscapedPegaReference(href);
  if (!decodedHref.includes('<pega-reference')) {
    return null;
  }

  const idMatch = /data-rule-id=["']([^"']+)["']/i.exec(decodedHref);
  if (!idMatch) {
    return null;
  }

  const namespaceMatch = /data-rule-namespace=["']([^"']+)["']/i.exec(decodedHref);
  const textMatch = /<pega-reference[^>]*>([\s\S]*?)<\/pega-reference>/i.exec(decodedHref);
  return {
    id: idMatch[1],
    text: textMatch?.[1]?.trim() ?? '',
    namespace: namespaceMatch?.[1]
  };
};

export function ImageEditDialog({
  isOpen,
  onClose,
  onSave,
  initialAttributes,
  target,
  imageCustomSource
}: ImageEditDialogProps) {
  const t = useI18n();
  const popoverRef = useRef(null);
  const wasOpenRef = useRef(false);
  const onEditSelectionChangeRef = useRef(imageCustomSource?.onEditSelectionChange);
  const onDismissEditRef = useRef(imageCustomSource?.onDismissEdit);
  const hasImageCustomSource = Boolean(imageCustomSource?.picker && imageCustomSource?.onSubmit);
  const canSubmitCustomSource = imageCustomSource?.canSubmit ?? true;
  const [alt, setAlt] = useState(initialAttributes.alt || '');
  const [width, setWidth] = useState(initialAttributes.width?.toString() || '');
  const [height, setHeight] = useState(initialAttributes.height?.toString() || '');
  const [actionUrl, setActionUrl] = useState(initialAttributes.actionUrl || '');
  const [actionUrlType, setActionUrlType] = useState<'url' | 'field'>(
    isFieldReferenceUrl(initialAttributes.actionUrl || '') ? 'field' : 'url'
  );
  const [customSize, setCustomSize] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(0);
  const [incorrectUrl, setIncorrectUrl] = useState(false);

  useEffect(() => {
    onEditSelectionChangeRef.current = imageCustomSource?.onEditSelectionChange;
    onDismissEditRef.current = imageCustomSource?.onDismissEdit;
  }, [imageCustomSource?.onEditSelectionChange, imageCustomSource?.onDismissEdit]);

  useEffect(() => {
    const isOpening = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;

    if (!isOpening) {
      return;
    }

    setAlt(initialAttributes.alt || '');
    setWidth(initialAttributes.width?.toString() || '');
    setHeight(initialAttributes.height?.toString() || '');

    // Normalize any escaped pega-reference markup before storing in state
    // to prevent double-escaping when the value is later serialized into href attributes.
    let normalizedActionUrl = initialAttributes.actionUrl || '';
    if (normalizedActionUrl && normalizedActionUrl.includes('&lt;pega-reference')) {
      normalizedActionUrl = normalizeEscapedPegaReference(normalizedActionUrl);
    }
    const hasFieldReference = hasImageCustomSource && isFieldReferenceUrl(normalizedActionUrl);
    if (hasFieldReference) {
      const customSourceValue = parseCustomSourceFromHref(normalizedActionUrl);
      if (customSourceValue) {
        onEditSelectionChangeRef.current?.(customSourceValue);
      }
      // Never leak pega-reference markup into URL input.
      setActionUrl('');
    } else {
      onEditSelectionChangeRef.current?.({ id: '', text: '', namespace: undefined });
      setActionUrl(normalizedActionUrl);
    }
    setActionUrlType(hasFieldReference ? 'field' : 'url');

    setCustomSize(false); // Always unchecked by default

    // Calculate aspect ratio from initial dimensions
    if (initialAttributes.width && initialAttributes.height) {
      setAspectRatio(initialAttributes.width / initialAttributes.height);
    }
    setIncorrectUrl(false);
  }, [isOpen, initialAttributes, hasImageCustomSource]);

  const onHeightChange = (incomingValue: string) => {
    const inputHeight = parseInt(incomingValue, 10);
    if (Number.isNaN(inputHeight)) {
      setHeight('');
      return;
    }
    setHeight(inputHeight.toString());
    if (aspectRatio) {
      setWidth(Math.floor(inputHeight * aspectRatio).toString());
    }
  };

  const onWidthChange = (incomingValue: string) => {
    const inputWidth = parseInt(incomingValue, 10);
    if (Number.isNaN(inputWidth)) {
      setWidth('');
      return;
    }
    setWidth(inputWidth.toString());
    if (aspectRatio) {
      setHeight(Math.floor(inputWidth / aspectRatio).toString());
    }
  };

  const handleSave = () => {
    if (incorrectUrl) return;

    const parsedWidth = parseFloat(width);
    const parsedHeight = parseFloat(height);
    let resolvedActionUrl: string | undefined;

    if (hasImageCustomSource && actionUrlType === 'field') {
      if (!imageCustomSource?.onSubmit) {
        return;
      }
      // Allow apply with no selected field to clear existing image action links.
      resolvedActionUrl = canSubmitCustomSource
        ? imageCustomSource.onSubmit(alt)?.trim() || undefined
        : undefined;
    } else {
      resolvedActionUrl = actionUrl.trim() || undefined;
    }

    const attrs: ImageAttributes = {
      src: initialAttributes.src,
      alt: alt.trim() || undefined,
      width: customSize && Number.isFinite(parsedWidth) ? parsedWidth : undefined,
      height: customSize && Number.isFinite(parsedHeight) ? parsedHeight : undefined,
      actionUrl: resolvedActionUrl
    };

    onSave(attrs);
    onDismissEditRef.current?.('apply');
    onClose();
  };

  const handleCancel = () => {
    onDismissEditRef.current?.('cancel');
    onClose();
  };

  if (!isOpen || !target) {
    return null;
  }

  return (
    <StyledFormDialog
      heading={t('rte_edit_image')}
      onSubmit={handleSave}
      onCancel={handleCancel}
      onDismiss={handleCancel}
      target={target}
      ref={popoverRef}
    >
      <Flex container={{ direction: 'column', gap: 2 }}>
        <Input
          label={t('description')}
          value={alt}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setAlt(e.target.value)}
          info={t('rte_image_description_info')}
        />
        {hasImageCustomSource && (
          <RadioButtonGroup
            name='editImageActionUrlType'
            label={t('rte_image_action_source')}
            onChange={e => {
              const { value } = e.target;
              if (value === 'url' || value === 'field') {
                setActionUrlType(value);
                if (value === 'field') {
                  setIncorrectUrl(false);
                }
              }
            }}
            inline
          >
            <RadioButton label={t('rte_link_url')} value='url' checked={actionUrlType === 'url'} />
            <RadioButton
              label={t('rte_link_field')}
              value='field'
              checked={actionUrlType === 'field'}
            />
          </RadioButtonGroup>
        )}
        {(!hasImageCustomSource || actionUrlType === 'url') && (
          <Input
            label={hasImageCustomSource ? t('rte_image_url') : t('rte_action_url')}
            value={actionUrl}
            info={incorrectUrl ? t('rte_invalid_url') : ''}
            status={incorrectUrl ? 'error' : undefined}
            onChange={e => {
              const actionUrlInput = e.target.value;
              setActionUrl(actionUrlInput);
              if (actionUrlInput === '') {
                setIncorrectUrl(false);
                return;
              }
              if (isFieldReferenceUrl(actionUrlInput)) {
                setIncorrectUrl(false);
                return;
              }
              try {
                const newUrl = new URL(actionUrlInput);
                setIncorrectUrl(!['http:', 'https:'].includes(newUrl.protocol));
              } catch {
                setIncorrectUrl(true);
              }
            }}
          />
        )}
        {hasImageCustomSource && actionUrlType === 'field' && imageCustomSource?.picker}
        <Checkbox
          checked={customSize}
          label={t('rte_image_custom_size')}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setCustomSize(e.target.checked);
          }}
        />
        {customSize && (
          <>
            <NumberInput
              label={t('rte_width_in_pixels')}
              value={Math.floor(Math.max(parseFloat(width) || 1, 1)).toString()}
              onChange={onWidthChange}
            />
            <NumberInput
              label={t('rte_height_in_pixels')}
              value={Math.floor(Math.max(parseFloat(height) || 1, 1)).toString()}
              onChange={onHeightChange}
            />
          </>
        )}
      </Flex>
    </StyledFormDialog>
  );
}
